/**
 * Pure view-selection logic for the tool-timeout recovery flow (a backend
 * tool call — course search / content lookup — timing out or erroring; see
 * src/components/assistant/tool-timeout-*.tsx). Mirrors the style of
 * lib/handoff-timeout-view.ts (plain function, no React, `as const`
 * object-literal enums) but is RICHER than that machine: handoff's
 * `nextTimeoutView(action)` is context-free (the target depends only on the
 * action, never on which view is showing), but this flow's BACK target from
 * CHOOSE genuinely depends on where the user came from — OFFER (first
 * attempt failed) or FAILED (a retry also failed) — so the transition
 * function has to take the current state as an input, not just the action.
 *
 * That "where did we come from" fact is carried as `origin` alongside
 * `view`, rather than doubling every downstream state into `_FROM_OFFER`/
 * `_FROM_FAILED` variants (CHOOSE_FROM_OFFER, MESSAGE_FROM_OFFER, ...) —
 * doubling would make MESSAGE/CALLBACK/etc. carry a distinction their own
 * UI never needs to render differently, purely to thread it back through a
 * later BACK. Keeping `origin` as a small piece of carried context (like a
 * reducer would) is the same information with none of the state explosion,
 * and it's still a pure, fully-deterministic function of ALL its inputs —
 * nothing here reaches into React state or a clock.
 */

export const ToolTimeoutView = {
  /** First failure — retry leads (filled primary), leave-details is the
   *  outline secondary. */
  OFFER: 'offer',
  /** A retry (from OFFER or FAILED) is in flight. */
  RETRYING: 'retrying',
  /** Retry succeeded — terminal for this machine; the caller swaps in the
   *  tool's real result via the tool-registry path (see
   *  tool-timeout-recovered.tsx). */
  RECOVERED: 'recovered',
  /** A retry itself failed — weighting flips: leave-details is now the
   *  filled primary, retry is demoted to a quiet secondary. */
  FAILED: 'failed',
  /** "How should we reach you?" — message vs call-back. Reachable from
   *  OFFER, FAILED, or mid-retry (RETRYING's "Skip & leave details"). */
  CHOOSE: 'choose',
  MESSAGE: 'message',
  CALLBACK: 'callback',
  /** Terminal. Split by type (rather than one SUBMITTED + a stored type)
   *  because that's what handoff-timeout-view.ts's own SUBMITTED does NOT
   *  need to do — that flow tracks the submitted type as separate component
   *  state (see handoff-timeout-panel.tsx's `submittedType`). This machine
   *  folds it into the view itself instead, since the SUBMITTED action
   *  already knows which form it came from (see nextToolTimeoutState). */
  SUBMITTED_MESSAGE: 'submitted_message',
  SUBMITTED_CALLBACK: 'submitted_callback',
  /** The card was ignored while the user kept talking — collapsed to a
   *  quiet inline chip (tool-timeout-chip.tsx). Never reachable from a
   *  terminal view (RECOVERED/SUBMITTED_*) — see IGNORE below. */
  COLLAPSED: 'collapsed',
} as const;
export type ToolTimeoutView =
  (typeof ToolTimeoutView)[keyof typeof ToolTimeoutView];

/** Which "lineage" CHOOSE (and anything reachable from it) belongs to —
 *  OFFER's first-attempt framing or FAILED's "still down" framing. Drives
 *  ONLY the BACK target; CHOOSE/MESSAGE/CALLBACK render identically either
 *  way. */
export const ToolTimeoutOrigin = {
  OFFER: 'offer',
  FAILED: 'failed',
} as const;
export type ToolTimeoutOrigin =
  (typeof ToolTimeoutOrigin)[keyof typeof ToolTimeoutOrigin];

export const ToolTimeoutAction = {
  /** OFFER "Try again" or FAILED "Try once more". */
  RETRY: 'retry',
  RETRY_SUCCEEDED: 'retry_succeeded',
  RETRY_FAILED: 'retry_failed',
  /** The model ended its turn without ever calling the tool (a realtime
   *  model cannot be forced to invoke a specific tool — see
   *  ToolRetryOutcomeEvent's ABANDONED member docstring in
   *  aol_schemas.ws_events). Distinct from RETRY_FAILED: nothing actually
   *  failed, so this must NOT re-anchor the lineage to FAILED — see its
   *  transition below. */
  RETRY_ABANDONED: 'retry_abandoned',
  /** The backend declined to run the retry at all — cap exhausted, an
   *  unknown/dead session, or the redrive itself threw before ever
   *  reaching the model (see ToolRetryOutcomeEvent's REJECTED member
   *  docstring in aol_schemas.ws_events). Distinct from BOTH siblings:
   *  not RETRY_FAILED (nothing was attempted, so — like RETRY_ABANDONED —
   *  this must NOT re-anchor the lineage to FAILED), and not
   *  RETRY_ABANDONED (there the model had the directive and chose not to
   *  call the tool; here the request to retry never even ran). The VIEW
   *  transition is identical to RETRY_ABANDONED's (return to `origin`,
   *  never RECOVERED) — only the hook layer's user-facing copy differs
   *  (useToolTimeoutFlow.ts sets an explicit error message for this one;
   *  ABANDONED sets none). retry_available is UNCHANGED by this action
   *  (it isn't even a field on the outcome event) — see that hook's file
   *  header for why continuing to honor the offer's existing
   *  retry_available, rather than inventing a second cap rule here, is
   *  the deliberate decision. */
  RETRY_REJECTED: 'retry_rejected',
  /** Session-recovery (reconnect reset): a genuine reconnect (not the
   *  initial connect) was observed while a retry was in flight (view ===
   *  RETRYING) for this incident — see useToolTimeoutFlow.ts's
   *  `retryStaleAfterReconnect` effect, the only place that dispatches
   *  this. The backend's `pending_tool_retry` bookkeeping is wiped on
   *  every disconnect and never repopulated on reconnect (a
   *  per-CONNECTION guarantee, not per-incident), so an outcome for the
   *  in-flight retry_id can now never arrive — waiting for one would spin
   *  forever. Distinct from RETRY_ABANDONED/RETRY_REJECTED: this is not
   *  an OUTCOME at all (nothing is known about whether the retry would
   *  have succeeded or failed) — the connection itself reset before an
   *  answer could ever come back. Same view transition as its two
   *  siblings regardless (return to `origin`, no lineage change — nothing
   *  here is a confirmed failure of the tool/service). ONLY ever
   *  dispatched from RETRYING — see the effect's own guard for why
   *  CHOOSE/MESSAGE/CALLBACK must not be interrupted by a reconnect that
   *  happens after the user has already moved on to leaving details. */
  CONNECTION_RESET: 'connection_reset',
  /** OFFER/FAILED "Leave my details", or RETRYING "Skip & leave details". */
  LEAVE_DETAILS: 'leave_details',
  CHOOSE_MESSAGE: 'choose_message',
  CHOOSE_CALLBACK: 'choose_callback',
  /** CHOOSE -> its origin (OFFER or FAILED); MESSAGE/CALLBACK -> CHOOSE. */
  BACK: 'back',
  /** MESSAGE -> SUBMITTED_MESSAGE; CALLBACK -> SUBMITTED_CALLBACK. */
  SUBMITTED: 'submitted',
  /** The card was ignored — collapse to the chip. */
  IGNORE: 'ignore',
  /** The chip's "Try again" tap — jumps straight back into a retry rather
   *  than re-expanding the OFFER card first (the chip only ever offers one
   *  affordance, so there's nothing to re-expand INTO). */
  RESUME: 'resume',
} as const;
export type ToolTimeoutAction =
  (typeof ToolTimeoutAction)[keyof typeof ToolTimeoutAction];

export interface ToolTimeoutState {
  view: ToolTimeoutView;
  origin: ToolTimeoutOrigin;
}

/** Fresh mount — first time this tool call has timed out for this turn. */
export const INITIAL_TOOL_TIMEOUT_STATE: ToolTimeoutState = {
  view: ToolTimeoutView.OFFER,
  origin: ToolTimeoutOrigin.OFFER,
};

const TERMINAL_VIEWS: ReadonlySet<ToolTimeoutView> = new Set([
  ToolTimeoutView.RECOVERED,
  ToolTimeoutView.SUBMITTED_MESSAGE,
  ToolTimeoutView.SUBMITTED_CALLBACK,
]);

/** Returns the state ToolTimeoutCard should show after `action` fires from
 *  `state`. Unlike handoff-timeout-view.ts's context-free lookup, this
 *  genuinely reads `state` (not just `action`) for BACK (see module header)
 *  and for RETRY_FAILED/LEAVE_DETAILS, which both need to know whether the
 *  CURRENT view is FAILED to decide the resulting origin. */
export function nextToolTimeoutState(
  state: ToolTimeoutState,
  action: ToolTimeoutAction
): ToolTimeoutState {
  const { view, origin } = state;

  // A terminal view (recovered / submitted) never transitions further —
  // the transcript row is done; a NEW timeout on a later tool call gets a
  // fresh mount at INITIAL_TOOL_TIMEOUT_STATE, not a transition from here.
  if (TERMINAL_VIEWS.has(view) && action !== ToolTimeoutAction.IGNORE) {
    return state;
  }

  switch (action) {
    case ToolTimeoutAction.RETRY:
      return { view: ToolTimeoutView.RETRYING, origin };

    case ToolTimeoutAction.RETRY_SUCCEEDED:
      return { view: ToolTimeoutView.RECOVERED, origin };

    case ToolTimeoutAction.RETRY_FAILED:
      // A fresh failure always re-anchors the lineage to FAILED, regardless
      // of what the retry's own origin was (OFFER's first try or an earlier
      // FAILED's "try once more" both land here the same way).
      return { view: ToolTimeoutView.FAILED, origin: ToolTimeoutOrigin.FAILED };

    case ToolTimeoutAction.RETRY_ABANDONED:
    case ToolTimeoutAction.RETRY_REJECTED:
    case ToolTimeoutAction.CONNECTION_RESET:
      // Same transition for all three: ABANDONED (the model had the
      // directive and chose not to call the tool), REJECTED (the retry
      // never even reached the model — the backend declined to run it at
      // all), and CONNECTION_RESET (the connection dropped before an
      // answer could ever come back) are all "nothing actually failed",
      // so none re-anchors the lineage to FAILED the way RETRY_FAILED
      // does — see each action's own doc comment for the distinction
      // between them (only useToolTimeoutFlow.ts's user-facing copy
      // differs, and CONNECTION_RESET is dispatched from a different
      // trigger entirely — see its own doc comment on why it's ONLY ever
      // reachable from RETRYING, unlike its two siblings). Returns to
      // whichever card the retry was launched FROM — OFFER on a
      // first-ever attempt, FAILED if an earlier attempt had already
      // failed once — same origin, no lineage change. RETRY_ABANDONED/
      // RETRY_REJECTED are reachable from ANY non-terminal view, not just
      // RETRYING: the caller correlates on retry_id (not on `view`)
      // before dispatching those, so an outcome arriving while the user
      // has since navigated to CHOOSE/MESSAGE/CALLBACK still resolves
      // correctly instead of being swallowed (session-recovery Finding 3
      // fix).
      return {
        view:
          origin === ToolTimeoutOrigin.FAILED
            ? ToolTimeoutView.FAILED
            : ToolTimeoutView.OFFER,
        origin,
      };

    case ToolTimeoutAction.LEAVE_DETAILS:
      // Origin is whichever lineage we're currently in. RETRYING itself
      // isn't an origin card, so it keeps whatever origin it was retrying
      // under (carried in `state.origin`, already correct — no branch
      // needed here beyond OFFER/FAILED's own view check).
      return {
        view: ToolTimeoutView.CHOOSE,
        origin:
          view === ToolTimeoutView.FAILED ? ToolTimeoutOrigin.FAILED : origin,
      };

    case ToolTimeoutAction.CHOOSE_MESSAGE:
      return { view: ToolTimeoutView.MESSAGE, origin };

    case ToolTimeoutAction.CHOOSE_CALLBACK:
      return { view: ToolTimeoutView.CALLBACK, origin };

    case ToolTimeoutAction.BACK:
      if (view === ToolTimeoutView.CHOOSE) {
        return {
          view:
            origin === ToolTimeoutOrigin.FAILED
              ? ToolTimeoutView.FAILED
              : ToolTimeoutView.OFFER,
          origin,
        };
      }
      // MESSAGE / CALLBACK -> CHOOSE, keeping the same origin so a second
      // BACK (from the re-shown CHOOSE) still resolves to the right card.
      return { view: ToolTimeoutView.CHOOSE, origin };

    case ToolTimeoutAction.SUBMITTED:
      return {
        view:
          view === ToolTimeoutView.CALLBACK
            ? ToolTimeoutView.SUBMITTED_CALLBACK
            : ToolTimeoutView.SUBMITTED_MESSAGE,
        origin,
      };

    case ToolTimeoutAction.IGNORE:
      return { view: ToolTimeoutView.COLLAPSED, origin };

    case ToolTimeoutAction.RESUME:
      return { view: ToolTimeoutView.RETRYING, origin };

    default:
      // Unreachable — exhaustive over ToolTimeoutAction. Same tooling note
      // as handoff-timeout-view.ts's nextTimeoutView: this repo's tsconfig
      // doesn't require a `never` cast here.
      return state;
  }
}
